import { useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { sendPresenceHeartbeat } from '@/lib/api/users';

/**
 * Interval between presence heartbeats while the tab is visible. Kept well below
 * the 5-minute "active" threshold so a focused tab stays green (Spec 525).
 */
export const HEARTBEAT_INTERVAL_MS = 60_000;

/**
 * Spec 525: presence heartbeat. While the user is signed in AND the tab is
 * visible (`document.visibilityState === 'visible'`), send a heartbeat on mount,
 * every {@link HEARTBEAT_INTERVAL_MS}, and immediately when the tab becomes
 * visible again. Pauses when the tab is hidden so "away from the panel" decays
 * to idle/offline (Teams/Slack semantics). Errors are swallowed — presence is
 * best-effort and must never surface to the user.
 *
 * Mount ONCE, above the routes (survives navigation). Runs for every role — we
 * track everyone's presence; the display is role-gated elsewhere.
 */
export function usePresenceHeartbeat(): void {
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated) return;

    let intervalId: number | undefined;

    const beat = () => {
      void sendPresenceHeartbeat().catch(() => {
        /* best-effort — ignore */
      });
    };

    const startTicking = () => {
      if (intervalId === undefined) {
        intervalId = window.setInterval(beat, HEARTBEAT_INTERVAL_MS);
      }
    };
    const stopTicking = () => {
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
        intervalId = undefined;
      }
    };

    // The interval genuinely rests while the tab is hidden and resumes (with an
    // immediate beat) when it becomes visible again.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        beat(); // (c) immediate on becoming visible
        startTicking();
      } else {
        stopTicking();
      }
    };

    // (a) on mount / login and (b) start the interval — only while visible.
    if (document.visibilityState === 'visible') {
      beat();
      startTicking();
    }
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      stopTicking();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isAuthenticated]);
}
